import { ArrowUpRight, ArrowDown } from "lucide-react";
import { BRAND } from "@/data/content";
import { track } from "@/lib/analytics";
import { useHoverParallax } from "@/lib/useHoverParallax";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Homepage hero — light editorial statement.
 * White canvas with a faint architectural grid, oversized serif display
 * with a grey italic accent, full-colour photograph with subtle cursor
 * parallax.
 */
export default function Hero({ onBook }) {
  const { ref, onMove, onLeave } = useHoverParallax(9);

  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 md:pt-44 pb-20 md:pb-24 bg-white overflow-hidden"
    >
      {/* architectural grid texture */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,0,0,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.035) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      {/* horizon hairline */}
      <div
        aria-hidden
        className="absolute top-24 md:top-32 left-0 right-0 h-px bg-gradient-to-r from-transparent via-black/15 to-transparent"
      />

      <div
        className={`${GRID} relative grid grid-cols-12 gap-8 md:gap-10 items-center lg:items-stretch`}
      >
        {/* Copy */}
        <div className="col-span-12 lg:col-span-7 relative z-10 stagger">
          <div data-testid="hero-badge" className="inline-flex items-center gap-3 mb-7 md:mb-9">
            <span className="inline-block h-px w-10 bg-black" aria-hidden />
            <span className="text-[11px] font-semibold tracking-[0.32em] uppercase text-zinc-700">
              Sales &amp; Marketing Transformation Specialist
            </span>
          </div>

          <h1
            data-testid="hero-heading"
            className="font-serif-display text-[44px] sm:text-6xl lg:text-[84px] leading-[1.02] tracking-tight text-black"
          >
            Turn your growth ambition into{" "}
            <em className="italic text-zinc-400">commercial success.</em>
          </h1>

          <p
            data-testid="hero-pitch"
            className="mt-8 max-w-xl text-base sm:text-lg text-zinc-600 leading-relaxed"
          >
            <span className="font-bold text-black">BLEZZINGS Growth Partners</span> connects
            commercial strategy, market intelligence, branding, sales and
            digital execution to help businesses accelerate profitable growth.
          </p>

          <p
            data-testid="hero-tagline"
            className="mt-6 font-serif-display italic text-xl sm:text-2xl text-black leading-snug"
          >
            {BRAND.tagline}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-book-btn"
              onClick={() => {
                track("book_consultation_open", { source: "hero" });
                onBook();
              }}
              className="group inline-flex items-center gap-3 h-12 px-7 bg-black text-white text-[15px] font-medium hover:bg-zinc-800 transition-colors duration-300"
            >
              Discuss Your Growth Challenge
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <a
              data-testid="hero-explore-link"
              href="#expertise"
              onClick={() => track("explore_expertise_click", { source: "hero" })}
              className="group inline-flex items-center gap-3 h-12 px-7 border border-black/20 text-black text-[15px] font-medium hover:border-black transition-colors duration-300"
            >
              Explore Our Expertise
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </div>
        </div>

        {/* Portrait visual */}
        <div
          className="col-span-12 lg:col-span-5 relative flex group"
          onMouseMove={onMove}
          onMouseLeave={onLeave}
        >
          <div
            data-testid="hero-visual"
            className="relative aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[460px] w-full overflow-hidden border border-black/10"
          >
            <div ref={ref} className="absolute inset-0 parallax-frame">
              <img
                data-testid="hero-portrait"
                src="/photos/band-strategy.jpg"
                alt="Indian business team in a working session around a table in a bright modern office"
                className="absolute inset-0 w-full h-full object-cover img-edit"
              />
            </div>
            <div className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-md px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-white">
              Strategy · Execution · Growth
            </div>
          </div>
          <div
            className="hidden lg:block absolute -z-0 -right-4 -bottom-4 w-40 h-40 border border-black/30"
            aria-hidden
          />
        </div>
      </div>
    </section>
  );
}
